/**
 * Cart (drawer + main cart page)
 * Handles add-to-cart, quantity updates, item removal,
 * drawer open/close, and section re-rendering.
 */

class ShopifyCart {
    constructor() {
        this.selectors = {
            drawer: '#cart-drawer',
            drawerPanel: '.cart-drawer__panel',
            closeBtn: '.cart-drawer__close, .cart-drawer__overlay',
            qtyBtn: '.cart-drawer__qty-btn, .cart-item__qty-btn',
            removeBtn: '.cart-drawer__item-remove, .cart-item__remove-btn',
            headerCart: '.header__cart',
            openTriggers: '[onclick="openCartDrawer()"], [data-drawer-trigger], .header__cart',
        };

        this.strings = (window.__theme || {}).strings || {};
        this.bindEvents();
        this.initAddToCartListeners();
    }

    /* ---------------------------------------------------
       Event binding
       --------------------------------------------------- */

    bindEvents() {
        document.addEventListener('click', (e) => this.handleClick(e));
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this.closeDrawer();
        });
    }

    initAddToCartListeners() {
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form.action && form.action.includes('/cart/add')) {
                e.preventDefault();
                this.handleAddToCart(form);
            }
        });
    }

    /* ---------------------------------------------------
       Click delegation
       --------------------------------------------------- */

    handleClick(e) {
        const target = e.target;

        // Open drawer
        if (target.closest(this.selectors.openTriggers) || target.closest('[aria-label="Cart"]')) {
            e.preventDefault();
            this.openDrawer();
            return;
        }

        // Close drawer
        if (target.closest(this.selectors.closeBtn)) {
            e.preventDefault();
            this.closeDrawer();
            return;
        }

        // Quantity update
        const qtyBtn = target.closest(this.selectors.qtyBtn);
        if (qtyBtn) {
            e.preventDefault();
            const line = qtyBtn.dataset.line;
            const qty = parseInt(qtyBtn.dataset.qty);
            if (line && !isNaN(qty)) this.updateItem(line, qty);
            return;
        }

        // Remove item
        const removeBtn = target.closest(this.selectors.removeBtn);
        if (removeBtn) {
            e.preventDefault();
            const line = this.getRemoveLine(removeBtn);
            if (line) {
                this.updateItem(line, 0);
            } else if (removeBtn.getAttribute('href')) {
                window.location.href = removeBtn.getAttribute('href');
            }
        }
    }

    /**
     * Extract line number from a remove button (data-line or href param).
     */
    getRemoveLine(btn) {
        if (btn.dataset.line) return btn.dataset.line;
        const href = btn.getAttribute('href');
        if (!href) return null;
        const url = new URL(href, window.location.origin);
        return url.searchParams.get('line');
    }

    /* ---------------------------------------------------
       Add to cart
       --------------------------------------------------- */

    async handleAddToCart(form) {
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn ? submitBtn.textContent : '';

        if (submitBtn) this.setButtonState(submitBtn, 'loading');

        const formData = new FormData(form);
        formData.append('sections', 'cart-drawer,header,cart');
        formData.append('sections_url', window.location.pathname);

        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData,
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            const data = await response.json();

            if (!response.ok) {
                this.resetButton(submitBtn, originalText);
                alert(data.description || 'Error adding to cart');
                return;
            }

            // Success
            if (submitBtn) {
                this.setButtonState(submitBtn, 'success');
                setTimeout(() => this.resetButton(submitBtn, originalText), 2000);
            }

            // Re-render sections and open drawer
            if (data.sections) {
                this.renderSections(data.sections);
            } else {
                await this.fetchSections();
            }
            this.openDrawer();
        } catch {
            this.resetButton(submitBtn, originalText);
        }
    }

    /* ---------------------------------------------------
       Button state helpers
       --------------------------------------------------- */

    setButtonState(btn, state) {
        if (!btn) return;
        btn.classList.remove('is-loading', 'is-success');
        if (state === 'loading') {
            btn.classList.add('is-loading');
            btn.textContent = this.strings.adding || 'Adding...';
            btn.disabled = true;
        } else if (state === 'success') {
            btn.classList.add('is-success');
            btn.textContent = this.strings.added || 'Added!';
        }
    }

    resetButton(btn, originalText) {
        if (!btn) return;
        btn.classList.remove('is-loading', 'is-success');
        btn.textContent = originalText;
        btn.disabled = false;
    }

    /* ---------------------------------------------------
       Drawer open / close
       --------------------------------------------------- */

    openDrawer() {
        const drawer = document.querySelector(this.selectors.drawer);
        if (!drawer) return;
        drawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';
    }

    closeDrawer() {
        const drawer = document.querySelector(this.selectors.drawer);
        if (!drawer) return;
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
    }

    /* ---------------------------------------------------
       Cart updates
       --------------------------------------------------- */

    setLoading(isLoading) {
        const panel = document.querySelector(this.selectors.drawerPanel);
        if (panel) panel.classList.toggle('is-loading', isLoading);

        const mainCart = document.querySelector('.cart-items');
        if (mainCart) mainCart.style.opacity = isLoading ? '0.5' : '1';
    }

    updateItem(line, quantity) {
        this.setLoading(true);

        fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
                line: parseInt(line),
                quantity,
                sections: 'cart-drawer,header,cart',
                sections_url: window.location.pathname,
            }),
        })
            .then(res => res.json())
            .then(data => this.renderSections(data.sections))
            .catch(() => {})
            .finally(() => this.setLoading(false));
    }

    fetchSections() {
        return fetch('/?sections=cart-drawer,header,cart')
            .then(res => res.json())
            .then(data => this.renderSections(data));
    }

    /* ---------------------------------------------------
       Section rendering
       --------------------------------------------------- */

    renderSections(sections) {
        if (!sections) return;
        this.replaceSection(sections['cart-drawer'], this.selectors.drawerPanel, this.selectors.drawer);
        this.replaceSection(sections['header'], this.selectors.headerCart);
        this.replaceSection(sections['cart'], '.cart-container');

        // Re-initialize carousels after section replacement (recommendations in footer)
        if (window.NatureNestCarousel) {
            const drawer = document.querySelector(this.selectors.drawer);
            if (drawer) {
                drawer.querySelectorAll('[data-carousel-container]').forEach((el) => {
                    new window.NatureNestCarousel(el);
                });
            }
        }
    }

    /**
     * Parse HTML string and replace a target element's content.
     * Tries primarySelector first, falls back to fallbackSelector.
     */
    replaceSection(html, primarySelector, fallbackSelector) {
        if (!html) return;

        const temp = document.createElement('div');
        temp.innerHTML = html;

        const newEl = temp.querySelector(primarySelector);
        const currentEl = document.querySelector(primarySelector);

        if (newEl && currentEl) {
            currentEl.innerHTML = newEl.innerHTML;
            return;
        }

        if (fallbackSelector) {
            const newFb = temp.querySelector(fallbackSelector);
            const curFb = document.querySelector(fallbackSelector);
            if (newFb && curFb) curFb.innerHTML = newFb.innerHTML;
        }
    }
}

// Global open/close helpers (used by onclick attributes in Liquid)
window.openCartDrawer = () => window.shopifyCart?.openDrawer();
window.closeCartDrawer = () => window.shopifyCart?.closeDrawer();

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    window.shopifyCart = new ShopifyCart();
});
